import { BuyersConsolidationQuoteServicesJSON, GenericLocationType } from 'features/BookingFlow'
import { Fragment, ReactNode } from 'react'
import { ISO } from 'service/ISO'
import { z } from 'zod'

export interface HeaderBehaviour {
  content: ReactNode
}

export interface SummaryBehaviour {
  content: ReactNode
  // type is used add key as the unique identifier for React to render
  type: string
}

export interface ServiceBehaviour {
  content: ReactNode
  // type is used add key as the unique identifier for React to render
  type: string
}

export interface FormFieldsBehaviour {
  content: ReactNode
}

export interface DefaultValuesBehaviour {
  defaultValues: Partial<MQFields>
}

export interface FormSchemaBehaviour<T extends z.ZodRawShape = {}> {
  schema: z.ZodObject<T>
}

type TransportToPortMode = 'truck' | 'intermodal' | 'barge'
export type AdditionalRequirements = {
  isHeavyTestedContainer: boolean
  letterOfCreditRequired: boolean
  maxPayloadContainer: string
  maxDemurrageTime: string
  EXWDate: string
  transportToPortMode: TransportToPortMode
  transportCompanyPreference: string
}

type MQCommonFields = {
  cargoReadyAt?: Date | null
  pickupAddress: string
  pickupLocation: GenericLocationType
  deliveryAddress: string
  deliveryLocation: GenericLocationType
  incoTerm: ISO.IncoTermsCode
  additionalRequirements: AdditionalRequirements
}

type MQDangerousGoodsFields = {
  additionalInfo?: string
}

type MQBuyersConsolidationFields = {
  buyersConsolidation?: BuyersConsolidationQuoteServicesJSON & {
    numberOfShipmentsUnknown: boolean
  }
}

export type MQFields = MQCommonFields & MQDangerousGoodsFields & MQBuyersConsolidationFields

class MQFormContentProcessorOptions {
  headerBehaviour!: HeaderBehaviour
  summaryBehaviour!: SummaryBehaviour[]
  servicesBehaviour!: ServiceBehaviour[]
  formSchemaBehaviour!: FormSchemaBehaviour[]
  formFieldsBehaviour!: FormFieldsBehaviour
  defaultValuesBehaviour!: DefaultValuesBehaviour[]
}

export class MQFormContentProcessor extends MQFormContentProcessorOptions {
  constructor({
    headerBehaviour,
    summaryBehaviour,
    servicesBehaviour = [],
    formSchemaBehaviour = [],
    formFieldsBehaviour,
    defaultValuesBehaviour = [],
  }: MQFormContentProcessorOptions) {
    super()
    this.headerBehaviour = headerBehaviour
    this.summaryBehaviour = summaryBehaviour
    this.servicesBehaviour = servicesBehaviour
    this.formSchemaBehaviour = formSchemaBehaviour
    this.formFieldsBehaviour = formFieldsBehaviour
    this.defaultValuesBehaviour = defaultValuesBehaviour
  }

  setHeaderBehaviour(behaviour: HeaderBehaviour): void {
    this.headerBehaviour = behaviour
  }

  setSummaryBehaviour(behaviour: SummaryBehaviour): void {
    this.summaryBehaviour = [behaviour]
  }

  setFormFieldsBehaviour(behaviour: FormFieldsBehaviour): void {
    this.formFieldsBehaviour = behaviour
  }

  addSummaryBehaviour(behaviour: SummaryBehaviour): void {
    this.summaryBehaviour.push(behaviour)
  }

  addFormSchemaBehaviour(behaviour: FormSchemaBehaviour): void {
    this.formSchemaBehaviour.push(behaviour)
  }

  addServiceBehaviour(behaviour: ServiceBehaviour): void {
    this.servicesBehaviour.push(behaviour)
  }

  addDefaultValuesBehaviour(behaviour: DefaultValuesBehaviour): void {
    this.defaultValuesBehaviour.push(behaviour)
  }

  get formSchema(): z.ZodObject<z.ZodRawShape> {
    const allSchemas = this.formSchemaBehaviour.map((behaviour) => behaviour.schema)
    const mergedValidationSchema = allSchemas.reduce((acc, schema) => {
      return acc.merge(schema)
    }, z.object({}))

    return mergedValidationSchema
  }

  get headerText(): ReactNode {
    return this.headerBehaviour.content
  }

  get summary(): ReactNode {
    return this.summaryBehaviour.map((behaviour) => {
      return <Fragment key={behaviour.type}>{behaviour.content}</Fragment>
    })
  }

  get services(): ReactNode[] {
    return this.servicesBehaviour.map((behaviour) => {
      return <Fragment key={behaviour.type}>{behaviour.content}</Fragment>
    })
  }

  get formFields(): ReactNode {
    return this.formFieldsBehaviour.content
  }

  get defaultValues(): Partial<MQFields> {
    return this.defaultValuesBehaviour.reduce((acc, behaviour) => {
      return {
        ...acc,
        ...behaviour.defaultValues,
      }
    }, {})
  }
}
